import React, { useState } from "react";
import { useDispatch } from "react-redux";
import SMBagActions from "actions/sm-bag-actions";
import RtrButton from "components/design-system/rtr-button";
import { openNotifyMeModal } from "slices/notifyMe-slice";
import NotifyBellWhite from "images/notify_bell_white.svg";
import { useAppSelector } from "../../../../../../util/store-types";
import { isSubscriptionMember } from "helpers/membership-helpers";
import { ctaText } from "rtr-constants";

type Address = {
  postalCode: string;
  default: boolean;
};

type Sku = {
  canonicalSize: string[];
};

type SelectedSize = {
  sku: Sku;
};

type StaticProduct = {
  productId: string;
  msrp: number;
};

type SubscriptionAddToBagButtonProps = {
  addresses: Address[];
  selectedSize: SelectedSize | null;
  staticProduct: StaticProduct;
  styleUnavailable: boolean;
  selectSizeIsUnavailable: boolean;
  availabilityLoaded?: boolean;
  membershipError?: boolean;
};

const SubscriptionAddToBagButton: React.FC<SubscriptionAddToBagButtonProps> = ({
  addresses,
  selectedSize,
  staticProduct,
  styleUnavailable,
  selectSizeIsUnavailable,
  availabilityLoaded,
  membershipError,
}) => {
  const dispatch = useDispatch();
  const [addingToBag, setAddingToBag] = useState(false);
  const isBagLoading = (useAppSelector(state => state.smBag?.smBagLoading) && addingToBag) || false;
  const userData = useAppSelector(state => state.userData);
  const disabled = styleUnavailable || selectSizeIsUnavailable || !selectedSize || isBagLoading || membershipError;

  const addToBag = () => {
    const defaultAddress = addresses?.find(address => address?.default === true);
    setAddingToBag(true);

    const handleAddToMembershipBagSuccess = () => {
      setAddingToBag(false);
    };
    const handleAddToMembershipBagFailure = () => {
      setAddingToBag(false);
    };

    const addSubscriptionItemToBag = SMBagActions.addBagItemPromise(
      {
        sku: selectedSize?.sku,
        type: "members",
        zipCode: defaultAddress?.postalCode,
      },
      handleAddToMembershipBagSuccess,
      handleAddToMembershipBagFailure,
      { productId: staticProduct.productId, retailPriceInteger: staticProduct.msrp }
    );
    dispatch(addSubscriptionItemToBag);
  };

  const canRenderNotifyMe =
    isSubscriptionMember(userData) &&
    availabilityLoaded &&
    (styleUnavailable || selectSizeIsUnavailable) &&
    !membershipError;

  if (canRenderNotifyMe) {
    return (
      <RtrButton
        style="primary"
        className="mt-8 mb-4"
        dataTestId="universal-notify-me-atb"
        dataHeapId="universal-atb"
        onClick={() => dispatch(openNotifyMeModal(selectedSize?.sku))}
        fullWidth>
        <NotifyBellWhite /> notify me
      </RtrButton>
    );
  }

  return (
    <RtrButton
      style="primary"
      className="mt-8 mb-4"
      dataTestId="updp-membership-atb"
      dataHeapId="updp-membership-atb"
      loading={isBagLoading}
      disabled={disabled}
      onClick={addToBag}
      fullWidth>
      {!selectedSize
        ? ctaText.CHOOSE_SIZE
        : isBagLoading
        ? ctaText.ADDING
        : styleUnavailable || membershipError
        ? ctaText.UNAVAILABLE
        : ctaText.ADD_TO_BAG}
    </RtrButton>
  );
};

export default SubscriptionAddToBagButton;
